"use client";
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; 

// 1️⃣ استيراد الأيقونات من lucide-react
import { 
  Globe, 
  Mail, 
  Phone, 
  ArrowUpLeft,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Sparkles
} from 'lucide-react';

// 2️⃣ تم إصلاح الكود هنا: إضافة كلمة import وقوس الفتح { الذي كان مفقوداً
import { 
  FaFacebookF, 
  FaInstagram, 
  FaWhatsapp,
  FaYoutube,
  FaGooglePlay,
  FaApple 
} from 'react-icons/fa';

export default function LaserJetEShop() {
  // حالة التحكم في زر "عرض المزيد"
  const [isExpanded, setIsExpanded] = useState(false);

  // مصفوفة بيانات الروابط الأساسية السريعة للمتجر
  const contactLinks = [
    {
      id: 1,
      label: "الموقع الإلكتروني للمتجر",
      value: "laserjet.com.eg",
      href: "https://laserjet.com.eg",
      icon: <Globe className="w-5 h-5 text-[#4f46e5]" />
    },
    {
      id: 2,
      label: "البريد الإلكتروني للدعم والاستفسار",
      value: "info@laserjet.com.eg",
      href: "mailto:info@laserjet.com.eg",
      icon: <Mail className="w-5 h-5 text-[#4f46e5]" />
    },
    {
      id: 3,
      label: "رقم خدمة العملاء الموحد",
      value: "01004331113",
      href: "tel:01004331113",
      icon: <Phone className="w-5 h-5 text-[#4f46e5]" />
    }
  ];

  // مصفوفة السوشيال ميديا الخاصة بالمتجر
  const socialLinks = [
    { id: 1, name: "فيسبوك", href: "https://www.facebook.com/laserjet.com.eg", icon: <FaFacebookF className="w-5 h-5" />, color: "bg-[#3b5998] text-white border-transparent hover:scale-105" },
    { id: 2, name: "واتساب المباشر", href: "https://wa.me/+201004331113", icon: <FaWhatsapp className="w-5 h-5" />, color: "bg-[#25D366] text-white border-transparent hover:scale-105" },
    { id: 3, name: "إنستغرام", href: "https://www.instagram.com/laserjet.com.eg/", icon: <FaInstagram className="w-5 h-5" />, color: "bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white border-transparent hover:scale-105" },
    { id: 4, name: "قناة واتساب", href: "https://whatsapp.com/channel/0029Va23H37GufIsQw29xG0a", icon: <FaWhatsapp className="w-5 h-5" />, color: "bg-[#25D366] text-white border-transparent hover:scale-105" },
    { id: 5, name: "يوتيوب", href: "https://www.youtube.com/@laserjet2458", icon: <FaYoutube className="w-5 h-5" />, color: "bg-[#ff0000] text-white border-transparent hover:scale-105" },
  ];

  // مصفوفة مميزات المتجر الإلكتروني
  const features = [
    { title: "تصفح ذكي للمنتجات", desc: "استكشف قائمة واسعة من الإلكترونيات والأجهزة المنزلية، مع تفاصيل تقنية دقيقة تساعدك على اتخاذ قرار الشراء." },
    { title: "سهولة إتمام الطلب", desc: "واجهة استخدام بسيطة تتيح لك اختيار منتجاتك وإتمام عملية الشراء في خطوات معدودة." },
    { title: "تعدّد خيارات الدفع", desc: "نوفر لك طرق دفع متنوعة وآمنة تناسب احتياجاتك المالية وتضمن سلامة تعاملاتك." },
    { title: "خدمة عملاء مخصصة", desc: "فريقنا جاهز للرد على استفساراتك ومساعدتك في اختيار المنتج الأنسب لك عبر كافة قنواتنا." },
    { title: "متابعة دقيقة للطلبات", desc: "تحديثات مستمرة واحترافية لحالة طلبك من لحظة تأكيد الشراء وحتى وصول المنتج إلى باب منزلك." },
    { title: "عروض وحصريات مستمرة", desc: "كن أول من يحصل على أحدث الخصومات، التصفيات، والعروض الحصرية على أجهزة LaserJet." }
  ];

  return (
    <motion.section 
      initial={{ y: 30 }} 
      whileInView={{ opacity: 1, y: 0 }} 
      viewport={{ once: true, margin: "-100px" }} 
      transition={{ duration: 0.6, ease: "easeOut" }} 
      className="w-full bg-gradient-to-br from-[#fbfbfe] via-[#f5f3ff] to-[#f0fdfa] relative py-12 md:py-24 px-4 md:px-8 font-sans overflow-hidden min-h-screen flex items-center justify-center"
      dir="rtl"
    >
      {/* ─── الخلفية الفاتحة المريحة والمتناسقة ─── */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e504_1px,transparent_1px),linear-gradient(to_bottom,#4f46e504_1px,transparent_1px)] bg-[size:36px_36px]" />

        <motion.div 
          animate={{ x: [0, -35, 0], y: [0, 35, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-[#4f46e5]/5 rounded-full blur-3xl"
        />
        <motion.div 
          animate={{ x: [0, 35, 0], y: [0, -35, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-0 -right-20 w-[450px] h-[450px] bg-[#00d2ff]/5 rounded-full blur-3xl"
        />
      </div>

      <div className="max-w-7xl mx-auto w-full relative z-10">
        
        {/* تقسيم الشاشة */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          
          {/* جهة اليمين: تفاصيل الخدمة وقنوات التواصل */}
          <div className="space-y-6 md:space-y-8 w-full overflow-hidden">
            
            {/* بلوك تفاصيل الخدمة المطورة */}
            <div className="space-y-5 text-right bg-white/90 backdrop-blur-md p-5 sm:p-8 rounded-[2rem] border border-purple-100 shadow-[0_20px_40px_-15px_rgba(79,70,229,0.04)]">
              
              {/* شارة علوية تلفت الانتباه */}
              <div className="inline-flex items-center gap-1.5 bg-[#4f46e5]/5 text-[#4f46e5] border border-[#4f46e5]/10 px-3 py-1 rounded-full text-[10px] font-black tracking-wide mb-1">
                <Sparkles className="w-3 h-3 text-[#7c3aed] animate-pulse" />
                <span>تجربة تسوق رقمية متكاملة</span>
              </div>

              {/* اسم الخدمة */}
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#4f46e5] tracking-wide flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="whitespace-nowrap">المتجر الإلكتروني</span>
                <span className="text-gray-300 font-light hidden sm:inline">|</span>
                <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-l from-[#4f46e5] to-purple-600 font-mono tracking-tight break-all">LaserJet E-Shop</span>
              </h3>
              
              {/* خط فاصل أنيق */}
              <div className="w-full h-[2px] bg-gradient-to-r from-[#4f46e5]/20 via-gray-100 to-transparent rounded-full my-3" />
              
              {/* وصف الخدمة */}
              <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-medium">
                وجهتك المتكاملة لتسوق أحدث الأجهزة الإلكترونية والأجهزة المنزلية بكل سهولة، من أي مكان وفي أي وقت. نوفر لك عبر <span className="font-bold text-gray-900">LaserJet E-Shop</span> تجربة شراء رقمية آمنة، سريعة، وموثوقة بالكامل.
              </p>

              {/* زر عرض المزيد / أقل التفاعلي */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#4f46e5] hover:text-purple-700 transition-colors mt-2 focus:outline-none group/toggle text-right"
              >
                <span>{isExpanded ? "عرض تفاصيل أقل" : "عرض مميزات المتجر وخيارات التسوق الذكي"}</span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 shrink-0 transition-transform group-hover/toggle:-translate-y-0.5" />
                ) : (
                  <ChevronDown className="w-4 h-4 shrink-0 transition-transform group-hover/toggle:translate-y-0.5" />
                )}
              </button>

              {/* المحتوى القابل للتمدد والانكماش */}
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden space-y-6 pt-2"
                  >
                    <div className="space-y-3">
                      <h4 className="text-sm sm:text-md font-bold text-gray-900 border-r-2 border-purple-500 pr-2">مميزات الخدمة للعملاء</h4>
                      <div className="grid grid-cols-1 gap-2.5">
                        {features.map((feat, idx) => (
                          <div key={idx} className="flex items-start gap-2.5 bg-slate-50/80 p-3 rounded-xl border border-slate-100">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs sm:text-sm font-bold text-gray-800">{feat.title}</p>
                              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-relaxed">{feat.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* لينك زيارة المتجر */}
              <div className="pt-2">
                <a 
                  href="https://laserjet.com.eg" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-bold text-white transition-all duration-300 group/btn bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] hover:from-[#4338ca] hover:to-[#6d28d9] px-5 sm:px-6 py-3 rounded-xl shadow-[0_10px_25px_-10px_rgba(79,70,229,0.4)] w-full sm:w-auto"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>زيارة المتجر الإلكتروني الآن</span>
                  <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover/btn:-translate-x-1" />
                </a>
              </div>
            </div>

            {/* كروت الروابط السريعة */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
             {contactLinks.map((item) => (
  <a
    key={item.id}
    href={item.href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-purple-500/5 text-gray-800 hover:border-[#4f46e5]/30 hover:shadow-[0_15px_30px_rgba(79,70,229,0.03)] transition-all duration-300 group/item overflow-hidden"
  >
    {/* 1. السهم في الأول على الشمال */}
    <ArrowUpLeft className="w-3.5 h-3.5 text-gray-300 group-hover/item:text-[#4f46e5] group-hover/item:-translate-x-0.5 group-hover/item:-translate-y-0.5 transition-all shrink-0" />
    
    {/* 2. الكلام بياخد المساحة اللي في النص كاملة بفضل flex-1 */}
    <div className="text-right overflow-hidden flex-1 min-w-0">
      <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium mb-0.5 truncate">{item.label}</p>
      <p className="text-xs font-bold text-gray-800 truncate tracking-wide text-left" dir="ltr">{item.value}</p>
    </div>

    {/* 3. الأيقونة في الآخر خالص على اليمين */}
    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 group-hover/item:bg-[#4f46e5]/5 transition-colors">
      {item.icon}
    </div>
  </a>
))}
            </div>

            {/* أزرار السوشيال ميديا */}
            <div className="p-4 sm:p-5 rounded-2xl bg-white border border-purple-500/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3.5">
              <h4 className="text-xs sm:text-sm font-bold text-gray-700">تابعنا لتصلك أحدث العروض والمنتجات:</h4>
              <div className="flex flex-wrap gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={social.name}
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-300 transform hover:-translate-y-0.5 shadow-sm border ${social.color}`}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* جهة اليسار: كارت الصورة الفخم */}
          <div className="w-full flex flex-col items-center lg:items-end lg:sticky lg:top-24 gap-4 sm:gap-6 mt-4 lg:mt-0">
            
            <div className="w-full max-w-[500px] h-[260px] sm:h-[440px] rounded-[2.5rem] overflow-hidden relative group/card bg-transparent sm:bg-white border-none sm:border sm:border-white shadow-none sm:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.06)] flex items-center justify-center p-0">
              <img 
                src="/laser-jet-shop.png" 
                alt="LaserJet Service Display" 
                className="w-full h-full object-contain sm:object-cover transition-transform duration-700 ease-out group-hover/card:scale-103 select-none pointer-events-none rounded-[2.5rem] sm:rounded-none"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 via-transparent to-transparent z-10 pointer-events-none rounded-[2.5rem]" />
              
              <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 z-20 bg-white/90 backdrop-blur-md px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-[#4f46e5] font-black text-[10px] sm:text-xs border border-white/50 shadow-sm">
                LaserJet Premium Store
              </div>
            </div>

            {/* أزرار المتاجر الرقمية تحت الصورة مباشرة */}
            <div className="w-full max-w-[500px] grid grid-cols-2 gap-3 sm:gap-4 px-1 sm:px-0">
              
              {/* زر جوجل بلاي */}
              <a 
                href="https://play.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-2xl bg-gray-900 text-white border border-gray-800 hover:bg-gray-800 transition-all duration-300 shadow-sm transform hover:-translate-y-0.5 group/store"
              >
                <FaGooglePlay className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover/store:text-purple-400 transition-colors shrink-0" />
                <div className="text-right min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 block font-medium leading-none mb-1 truncate">تحميل من متجر</p>
                  <p className="text-xs sm:text-sm font-bold leading-none font-sans truncate">Google Play</p>
                </div>
              </a>

              {/* زر آبل ستور */}
              <a 
                href="https://apps.apple.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-2xl bg-gray-900 text-white border border-gray-800 hover:bg-gray-800 transition-all duration-300 shadow-sm transform hover:-translate-y-0.5 group/store"
              >
                <FaApple className="w-5 h-5 sm:w-6 sm:h-6 text-white group-hover/store:text-purple-400 transition-colors shrink-0" />
                <div className="text-right min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-gray-400 block font-medium leading-none mb-1 truncate">تحميل من متجر</p>
                  <p className="text-xs sm:text-sm font-bold leading-none font-sans truncate">App Store</p>
                </div>
              </a>

            </div>

          </div>

        </div>

      </div>
    </motion.section>
  );
}